import React, { FC, useState } from 'react';
import { connect } from 'react-redux';
import moment from 'moment';
import { Button, DatePicker, Form, Input, Modal, Select, Switch } from 'antd';
import { UploadOutlined } from '@ant-design/icons';
import OWUpload from '../OWUpload';
import { complianceTypesRestCrudThunks } from '../../thunks/compliance_types_thunks';
import { complianceDocumentsRestCrudThunks } from '../../thunks/compliance_documents_thunks';
import { fetchComplianceClientStatuses } from '../../thunks/compliance_statuses_thunks';
import { getRecordsForTargetCollection } from '../../reducers/standard_reducer_utils';
import { ROLE_TYPES } from '../../utils/DataConstants';
import { ComplianceDocument, ComplianceType } from '../../types/ComplianceTypes';
import { nullSafeGet, nullSafeGetOrElse } from '../../utils/DataAccessUtils';
import { getUploadedFileUrl } from '../../utils/FileAccessUtils';
import useMountEffect from '../../hooks/useMountEffect';

const TYPES_DROPDOWN_TARGET_COLLECTION = 'complianceTypesDropdown';

const STEPS = { SCOPE: 'scope', DETAILS: 'details' };

interface AddComplianceDocumentModalProps {
	visible: boolean;
	editingDocument?: ComplianceDocument;
	// Opens the edit modal straight on the Details step (where the file upload
	// lives) so the primary action reads as swapping the file. There is no
	// dedicated backend replace endpoint — replace is a re-upload + PUT /documents.
	replaceMode?: boolean;
	onCancel: () => void;
	onSuccess: () => void;
	complianceTypes: ComplianceType[];
	saving: boolean;
	fetchDropdownData: () => void;
	fetchClientStatuses: () => Promise<any[]>;
	createComplianceDocument: (entity: any) => Promise<any>;
	updateComplianceDocument: (entity: any) => Promise<any>;
}

const AddComplianceDocumentModal: FC<AddComplianceDocumentModalProps> = ({
	visible,
	editingDocument,
	replaceMode,
	onCancel,
	onSuccess,
	complianceTypes,
	saving,
	fetchDropdownData,
	fetchClientStatuses,
	createComplianceDocument,
	updateComplianceDocument,
}): React.ReactElement => {
	const [form] = Form.useForm();
	const [step, setStep] = useState(replaceMode ? STEPS.DETAILS : STEPS.SCOPE);
	const [documentFileList, setDocumentFileList] = useState([]);
	const [clients, setClients] = useState([]);
	const isEditing = !!editingDocument;

	// Clients (customers) for the sharing picker come from the compliance
	// client_statuses list (buyerCompanyId + buyerCompanyName).
	useMountEffect(() => {
		fetchDropdownData();
		fetchClientStatuses().then((clientStatuses) => setClients(clientStatuses || []));
	});

	// Compliance type is optional, so the SCOPE step has nothing to validate —
	// just advance to DETAILS.
	const handleNext = () => setStep(STEPS.DETAILS);

	const handleBack = () => setStep(STEPS.SCOPE);

	const resetAndClose = (callback) => {
		form.resetFields();
		setStep(replaceMode ? STEPS.DETAILS : STEPS.SCOPE);
		setDocumentFileList([]);
		callback();
	};

	const handleCancel = () => resetAndClose(onCancel);

	const handleSubmit = () => {
		form.validateFields().then((values) => {
			const uploadedFile = documentFileList
				.filter((file) => file.status === 'done')
				.map((file) => ({
					fileUrl: getUploadedFileUrl(file),
					fileName: file.name,
				}))
				.filter((file) => file.fileUrl)[0];
			const existingFileUrl = nullSafeGet('fileUrl', editingDocument);
			if (!uploadedFile && !existingFileUrl) {
				form.setFields([
					{ name: 'documentUpload', errors: ['Please upload the compliance document.'] },
				]);
				return;
			}
			// "Select all" is a UI-only toggle: expand it to the explicit list of
			// every loaded client's buyer-company id. There is no all-clients flag —
			// buyerCompanyIds is always the concrete pre-authorization list.
			const buyerCompanyIds = values.shareWithAllClients
				? clients.map((client) => client.buyerCompanyId)
				: nullSafeGetOrElse('buyerCompanyIds', values, []);
			const entity = {
				countryCode: 'US',
				complianceTypeId: values.complianceTypeId ? values.complianceTypeId : undefined,
				name: values.name,
				buyerCompanyIds,
				effectiveDate: values.effectiveDate ? values.effectiveDate.toISOString() : undefined,
				expiryDate: values.expiryDate ? values.expiryDate.toISOString() : undefined,
				fileUrl: uploadedFile ? uploadedFile.fileUrl : existingFileUrl,
				fileName: uploadedFile ? uploadedFile.fileName : nullSafeGet('fileName', editingDocument),
			};
			const save = isEditing
				? updateComplianceDocument({ ...editingDocument, ...entity })
				: createComplianceDocument(entity);
			save.then(() => resetAndClose(onSuccess));
		});
	};

	const handleDocumentChange = (info) => setDocumentFileList(info.fileList);

	const typeOptions = complianceTypes.map((complianceType) => (
		<Select.Option key={complianceType.id} value={complianceType.id}>
			{complianceType.name}
		</Select.Option>
	));

	const footer =
		step === STEPS.SCOPE
			? [
					<Button key="cancel" onClick={handleCancel}>
						CANCEL
					</Button>,
					<Button key="next" type="primary" onClick={handleNext}>
						NEXT
					</Button>,
			  ]
			: [
					<Button key="back" onClick={handleBack}>
						BACK
					</Button>,
					<Button key="save" type="primary" loading={saving} onClick={handleSubmit}>
						{isEditing ? 'SAVE' : 'ADD DOCUMENT'}
					</Button>,
			  ];

	return (
		<Modal
			visible={visible}
			title={
				replaceMode
					? 'Replace Compliance Document'
					: isEditing
					? 'Edit Compliance Document'
					: 'Add Compliance Document'
			}
			onCancel={handleCancel}
			footer={footer}
			destroyOnClose={true}
			width={564}
		>
			<Form
				form={form}
				layout="vertical"
				preserve={false}
				initialValues={{
					complianceTypeId: nullSafeGet('complianceTypeId', editingDocument),
					name: nullSafeGet('name', editingDocument),
					shareWithAllClients: false,
					buyerCompanyIds: nullSafeGetOrElse('buyerCompanyIds', editingDocument, []),
					effectiveDate: nullSafeGet('effectiveDate', editingDocument)
						? moment(nullSafeGet('effectiveDate', editingDocument))
						: undefined,
					expiryDate: nullSafeGet('expiryDate', editingDocument)
						? moment(nullSafeGet('expiryDate', editingDocument))
						: undefined,
				}}
			>
				<div className={step === STEPS.SCOPE ? '' : 'hidden'}>
					<Form.Item
						name="complianceTypeId"
						label="Compliance type (optional)"
						extra="Pick a compliance type your clients request."
					>
						<Select
							showSearch={true}
							optionFilterProp="children"
							placeholder="Certificate of Insurance"
							allowClear={true}
						>
							{typeOptions}
						</Select>
					</Form.Item>
				</div>
				<div className={step === STEPS.DETAILS ? '' : 'hidden'}>
					<Form.Item
						name="name"
						label="Document Name"
						rules={[{ required: true, message: 'Please name this document.' }]}
					>
						<Input />
					</Form.Item>
					<div className="flex flex-row gap-3">
						<Form.Item name="effectiveDate" label="Effective date" className="flex-1">
							<DatePicker className="w-full" format="D MMM, YYYY" />
						</Form.Item>
						<Form.Item name="expiryDate" label="Expiration date" className="flex-1">
							<DatePicker className="w-full" format="D MMM, YYYY" />
						</Form.Item>
					</div>
					<div className="mb-1 flex flex-row items-center justify-between">
						<span>Client(s)</span>
						<span className="flex flex-row items-center gap-2">
							<span className="text-character-secondary">Select all</span>
							<Form.Item name="shareWithAllClients" valuePropName="checked" noStyle={true}>
								<Switch size="small" />
							</Form.Item>
						</span>
					</div>
					<Form.Item noStyle={true} shouldUpdate={true}>
						{() => (
							<Form.Item
								name="buyerCompanyIds"
								extra="Clients added will automatically get access to this document if they request it."
							>
								<Select
									mode="multiple"
									optionFilterProp="children"
									placeholder={
										form.getFieldValue('shareWithAllClients') ? 'All clients' : 'Select clients'
									}
									disabled={!!form.getFieldValue('shareWithAllClients')}
								>
									{clients.map((client) => (
										<Select.Option key={client.buyerCompanyId} value={client.buyerCompanyId}>
											{nullSafeGetOrElse('buyerCompanyName', client, '')}
										</Select.Option>
									))}
								</Select>
							</Form.Item>
						)}
					</Form.Item>
					<Form.Item name="documentUpload" label="Document Upload" required={!isEditing}>
						<OWUpload fileList={documentFileList} onChange={handleDocumentChange} maxCount={1}>
							<Button icon={<UploadOutlined />}>
								{isEditing ? 'Replace document' : 'Click to Upload'}
							</Button>
						</OWUpload>
					</Form.Item>
				</div>
			</Form>
		</Modal>
	);
};

const mapStateToProps = (state) => ({
	complianceTypes: getRecordsForTargetCollection(
		state.compliance_types,
		TYPES_DROPDOWN_TARGET_COLLECTION
	),
	saving: state.compliance_documents.creating || state.compliance_documents.updating,
});

const mapDispatchToProps = (dispatch) => ({
	fetchDropdownData: () => {
		dispatch(
			complianceTypesRestCrudThunks(ROLE_TYPES.SUPPLIER).read({}, TYPES_DROPDOWN_TARGET_COLLECTION)
		);
	},
	fetchClientStatuses: () => dispatch(fetchComplianceClientStatuses(ROLE_TYPES.SUPPLIER)()),
	createComplianceDocument: (entity) =>
		dispatch(complianceDocumentsRestCrudThunks(ROLE_TYPES.SUPPLIER).create(entity)),
	updateComplianceDocument: (entity) =>
		dispatch(complianceDocumentsRestCrudThunks(ROLE_TYPES.SUPPLIER).update(entity)),
});

export default connect(mapStateToProps, mapDispatchToProps)(AddComplianceDocumentModal);
